/**
 * Build minimal page/device context without collecting user content.
 */
import { readAttributionParams } from "./attribution";
import type { PixelConfig } from "./types";
import { safeDocument, safeNavigator } from "./utils";

export type ContextPayload = {
  page: {
    url?: string;
    referrer?: string;
    title?: string;
    path?: string;
    host?: string;
  };
  device: {
    lang?: string;
    tz_offset_min?: number;
    screen?: [number, number];
    viewport?: [number, number];
    device_class?: "mobile" | "desktop";
  };
  marketing: Record<string, string>;
  network: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  };
};

export function buildContext(
  config: PixelConfig,
  sanitizeUrl: (url?: string) => string | undefined,
): ContextPayload {
  const doc = safeDocument();
  const nav = safeNavigator();
  const location = typeof window === "undefined" ? undefined : window.location;

  const url = location?.href;
  const referrer = doc?.referrer;
  const sanitizedUrl = sanitizeUrl(url);
  const sanitizedReferrer = sanitizeUrl(referrer ?? undefined);

  const marketing = url ? readAttributionParams(url) : {};

  const screenSize: [number, number] | undefined =
    typeof screen !== "undefined" ? [screen.width, screen.height] : undefined;
  const viewport: [number, number] | undefined =
    typeof window !== "undefined"
      ? [window.innerWidth, window.innerHeight]
      : undefined;
  const deviceClass = viewport
    ? viewport[0] < 768
      ? "mobile"
      : "desktop"
    : undefined;

  const network = (nav as any)?.connection
    ? {
        effectiveType: (nav as any).connection.effectiveType,
        downlink: (nav as any).connection.downlink,
        rtt: (nav as any).connection.rtt,
      }
    : {};

  return {
    page: {
      url: sanitizedUrl,
      referrer: sanitizedReferrer,
      title: doc?.title,
      path: location?.pathname,
      host: location?.hostname,
    },
    device: {
      lang: nav?.language,
      tz_offset_min: new Date().getTimezoneOffset(),
      screen: screenSize,
      viewport,
      device_class: deviceClass,
    },
    marketing,
    network,
  };
}
